import axios from 'axios';
import * as authentication from 'models/authentication';
import * as jwt from 'models/jwt';
import { useCallback, useEffect, useMemo } from 'react';
import useLocalStorage from './useLocalStorage';
import useSessionStorage from './useSessionStorage';

export type ReturnType = [
  jwt.identity.IJWTIdentity | undefined,
  (value: authentication.LoginResult | undefined, remember?: boolean) => void,
  authentication.LoginResult | undefined
];

/**
 * A hook that returns the currently logged in user by JWT.
 * @returns The user parsed from the JWT.
 */
export const useJWT = (): ReturnType => {
  const [local, setLocal] = useLocalStorage<authentication.LoginResult | undefined>(
    'user',
    undefined,
  );
  const [session, setSession] = useSessionStorage<
    authentication.LoginResult | undefined
  >('user', undefined);

  const value = useMemo(() => {
    if (session) return jwt.identity.Parse(session.token);
    if (local) return jwt.identity.Parse(local.token);
    return undefined;
  }, [local, session]);

  useEffect(() => {
    const tokens = session ?? local;
    if (tokens) {
      axios.defaults.headers.common.Authorization = `Bearer ${tokens.token}`;
    }
  }, [session, local]);

  const setValue = useCallback((
    newValue: authentication.LoginResult | undefined,
    remember?: boolean,
  ) => {
    if (!newValue) {
      setSession(undefined);
      setLocal(undefined);
      return;
    }
    if (session) setSession(newValue);
    else if (local) setLocal(newValue);
    else if (remember) setLocal(newValue);
    else setSession(newValue);

    axios.defaults.headers.common.Authorization = `Bearer ${newValue.token}`;
  }, []);

  const tokensValue = useMemo(() => {
    if (session) return session;
    if (local) return local;
    return undefined;
  }, [session, local]);

  return [value, setValue, tokensValue];
};

export default useJWT;
